import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MoireMark } from "@/components/MoireLogo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

function Shell({ overline, title, children }) {
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm">
        <div className="flex justify-center mb-6"><MoireMark size={44} /></div>
        <p className="font-sans text-[10px] tracking-luxe uppercase text-[#C8A97E] text-center">{overline}</p>
        <h1 className="font-serif text-4xl text-[#F5F2EA] text-center mt-2 mb-8">{title}</h1>
        {children}
      </motion.div>
    </div>
  );
}

const field = "bg-transparent border-0 border-b border-[#F5F2EA]/30 rounded-none text-[#F5F2EA] placeholder:text-[#8A8170] px-0 py-3 focus-visible:ring-0 focus-visible:border-[#C8A97E]";
const submit = "w-full bg-[#C8A97E] text-[#0D0D0D] py-3.5 font-sans text-xs tracking-luxe uppercase hover:bg-[#E5C293] transition-colors mt-8 disabled:opacity-50";

export function SignIn() {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const { login } = useAuth(); const navigate = useNavigate();
  const onSubmit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await login(email, password); navigate("/match"); }
    catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Sign in failed."); }
    finally { setLoading(false); }
  };
  return (
    <Shell overline="Welcome back" title="Sign In">
      <form onSubmit={onSubmit} className="space-y-5" data-testid="signin-form">
        <Input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} data-testid="signin-email" />
        <Input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} data-testid="signin-password" />
        {err && <p className="font-sans text-xs text-[#C8A97E]" data-testid="signin-error">{err}</p>}
        <button type="submit" disabled={loading} className={submit} data-testid="signin-submit">{loading ? "…" : "Sign In"}</button>
      </form>
      <div className="flex justify-between mt-6">
        <Link to="/forgot-password" className="font-sans text-xs text-[#8A8170] hover:text-[#C8A97E]">Forgot password?</Link>
        <Link to="/signup" className="font-sans text-xs text-[#8A8170] hover:text-[#C8A97E]" data-testid="link-signup">Create account</Link>
      </div>
    </Shell>
  );
}

export function SignUp() {
  const [name, setName] = useState(""); const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const { register } = useAuth(); const navigate = useNavigate();
  const onSubmit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await register(email, password, name); navigate("/match"); }
    catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Sign up failed."); }
    finally { setLoading(false); }
  };
  return (
    <Shell overline="Join MOIRÉ" title="Create Account">
      <form onSubmit={onSubmit} className="space-y-5" data-testid="signup-form">
        <Input type="text" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className={field} data-testid="signup-name" />
        <Input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} data-testid="signup-email" />
        <Input type="password" required minLength={6} placeholder="Password (min 6)" value={password} onChange={(e) => setPassword(e.target.value)} className={field} data-testid="signup-password" />
        {err && <p className="font-sans text-xs text-[#C8A97E]" data-testid="signup-error">{err}</p>}
        <button type="submit" disabled={loading} className={submit} data-testid="signup-submit">{loading ? "…" : "Create Account"}</button>
      </form>
      <p className="text-center mt-6"><Link to="/signin" className="font-sans text-xs text-[#8A8170] hover:text-[#C8A97E]" data-testid="link-signin">Already have an account? Sign in</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState(""); const [sent, setSent] = useState(false); const [loading, setLoading] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch {} finally { setLoading(false); }
  };
  return (
    <Shell overline="Reset" title="Forgot Password">
      {sent ? (
        <p className="font-sans text-sm text-[#F5F2EA]/70 text-center leading-relaxed">If that email exists, a reset link is on its way.</p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form">
          <Input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} data-testid="forgot-email" />
          <button type="submit" disabled={loading} className={submit} data-testid="forgot-submit">{loading ? "…" : "Send Reset Link"}</button>
        </form>
      )}
      <p className="text-center mt-6"><Link to="/signin" className="font-sans text-xs text-[#8A8170] hover:text-[#C8A97E]">Back to sign in</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const [password, setPassword] = useState(""); const [done, setDone] = useState(false); const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");
  const onSubmit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await api.post("/auth/reset-password", { token, password }); setDone(true); setTimeout(() => navigate("/signin"), 1500); }
    catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <Shell overline="Reset" title="New Password">
      {done ? <p className="font-sans text-sm text-[#C8A97E] text-center">Password updated. Redirecting…</p> : (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
          <Input type="password" required minLength={6} placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} data-testid="reset-password" />
          {err && <p className="font-sans text-xs text-[#C8A97E]">{err}</p>}
          <button type="submit" disabled={loading} className={submit} data-testid="reset-submit">{loading ? "…" : "Update Password"}</button>
        </form>
      )}
    </Shell>
  );
}
